import React from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddBoxIcon from "@mui/icons-material/AddBox";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import { Link, useNavigate } from "react-router-dom";
import { formatDateTimeSecondsSk } from "app/utils/dateFormat";
import { deleteUserById, getUsers } from "app/services/api/users";

const resolveUserId = (u) =>
  u?._id ?? u?.id ?? u?.userId ?? u?.user_id ?? u?.uid ?? null;

const resolveText = (u, keys) => {
  for (const k of keys) {
    const v = u?.[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
};

const resolveStatusValue = (u) => {
  const raw = u?.status ?? u?.state ?? null;
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw === "boolean") return raw ? 1 : 0;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw === 0 ? 0 : 1;
  const n = Number(String(raw).trim());
  if (Number.isFinite(n)) return n === 0 ? 0 : 1;
  return null;
};

const getStatusLabel = (t, value) => {
  if (value === 1) {
    return t("pages.dsokUsers.status.active", { defaultValue: "Active" });
  }
  if (value === 0) {
    return t("pages.dsokUsers.status.inactive", { defaultValue: "Inactive" });
  }
  return "—";
};

const resolveUserTypeValue = (u) => {
  const raw = u?.typ ?? u?.type ?? u?.role ?? null;
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const n = Number(String(raw).trim());
  return Number.isFinite(n) ? n : null;
};

const getUserTypeLabel = (t, value, fallbackRaw = "") => {
  const v = Number(value);
  if ([3, 4, 5, 6].includes(v)) {
    return t(`pages.dsokUsers.form.userTypes.${v}`, { defaultValue: String(v) });
  }
  const raw = String(fallbackRaw ?? "").trim();
  return raw || "—";
};

const resolveType = (t, u) => {
  const raw = resolveText(u, ["typ", "type", "role"]) || "";
  const v = resolveUserTypeValue(u);
  return getUserTypeLabel(t, v, raw);
};
const resolveLastLogin = (u) =>
  u?.last_login ?? u?.lastLogin ?? u?.lastLoginAt ?? null;

const resolveName = (u) => {
  const first = resolveText(u, [
    "first_name",
    "firstName",
    "firstname",
    "name",
  ]);
  const last = resolveText(u, ["last_name", "lastName", "lastname", "surname"]);
  const full = `${first} ${last}`.trim();
  if (full) return full;
  return resolveText(u, ["full_name", "fullName", "displayName"]) || "";
};

const normalizeUsersResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.users)) return data.users;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const DSOKUsers = () => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const { enqueueSnackbar } = useSnackbar();
  const { showDialog, hideDialog } = useJumboDialog();
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [items, setItems] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [deletingId, setDeletingId] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUsers({ page: 1, limit: 20 });
      setItems(normalizeUsersResponse(data));
    } catch (e) {
      console.error(e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const filtered = React.useMemo(() => {
    const needle = String(q || "")
      .trim()
      .toLowerCase();
    if (!needle) return items;
    return (Array.isArray(items) ? items : []).filter((u) => {
      const statusValue = resolveStatusValue(u);
      const hay = [
        resolveText(u, ["username", "userName", "login", "name"]),
        resolveText(u, ["email", "mail"]),
        resolveName(u),
        resolveType(t, u),
        getStatusLabel(t, statusValue),
        String(resolveUserId(u) ?? ""),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [items, q, t]);

  const onCreate = () => navigate("/customer/dsok/users/new");
  const onEdit = (id) =>
    navigate(`/customer/dsok/users/${encodeURIComponent(String(id))}`);

  const onDelete = (u) => {
    const id = resolveUserId(u);
    if (!id) return;
    if (String(deletingId ?? "") === String(id)) return;
    const username =
      resolveText(u, ["username", "userName", "login"]) || String(id);
    let confirmed = false;

    showDialog({
      variant: "confirm",
      title: t("pages.dsokUsers.delete.title", { defaultValue: "Delete user" }),
      content: (
        <Typography variant="body2">
          {t("pages.dsokUsers.delete.content", {
            username,
            defaultValue: `Are you sure you want to delete "${username}"?`,
          })}
        </Typography>
      ),
      onYes: async () => {
        if (confirmed) return;
        confirmed = true;
        hideDialog();
        setDeletingId(id);
        try {
          await deleteUserById(id);
          enqueueSnackbar(
            t("pages.dsokUsers.delete.success", {
              defaultValue: "User deleted.",
            }),
            { variant: "success" },
          );
          await load();
        } catch (e) {
          console.error(e);
          enqueueSnackbar(
            t("pages.dsokUsers.delete.error", {
              defaultValue: "Failed to delete user.",
            }),
            { variant: "error" },
          );
        } finally {
          setDeletingId(null);
        }
      },
      onNo: () => {},
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Typography color="text.primary">
          {t("pages.dsokUsers.title", { defaultValue: "Users" })}
        </Typography>
      </Breadcrumbs>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h4">
          {t("pages.dsokUsers.title", { defaultValue: "Users" })}
        </Typography>
        <Button
          variant="contained"
          onClick={onCreate}
          startIcon={<AddBoxIcon />}
        >
          {t("pages.dsokUsers.add", { defaultValue: "Add" })}
        </Button>
      </Stack>

      <Card sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <TextField
            fullWidth
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("pages.dsokUsers.search", {
              defaultValue: "Search…",
            })}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
            size="small"
          />
          <Button variant="outlined" onClick={load} disabled={loading}>
            {t("buttons.refresh", { defaultValue: "Refresh" })}
          </Button>
        </Stack>

        {loading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={18} />
            <Typography variant="body2">
              {t("common.loading", { defaultValue: "Loading..." })}
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error">
            {t("pages.dsokUsers.loadError", {
              defaultValue: "Failed to load users.",
            })}
          </Alert>
        ) : filtered.length === 0 ? (
          <Alert severity="info">
            {t("pages.dsokUsers.empty", { defaultValue: "No users found." })}
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("pages.dsokUsers.table.username", {
                    defaultValue: "Username",
                  })}
                </TableCell>
                <TableCell>
                  {t("pages.dsokUsers.table.email", { defaultValue: "Email" })}
                </TableCell>
                <TableCell>
                  {t("pages.dsokUsers.table.name", { defaultValue: "Name" })}
                </TableCell>
                <TableCell>
                  {t("pages.dsokUsers.table.type", { defaultValue: "Type" })}
                </TableCell>
                <TableCell>
                  {t("pages.dsokUsers.table.status", {
                    defaultValue: "Status",
                  })}
                </TableCell>
                <TableCell>
                  {t("pages.dsokUsers.table.lastLogin", {
                    defaultValue: "Last login",
                  })}
                </TableCell>
                <TableCell align="right">
                  {t("pages.dsokUsers.table.actions", {
                    defaultValue: "Actions",
                  })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((u, idx) => {
                const id = resolveUserId(u);
                const isDeleting =
                  id && String(deletingId ?? "") === String(id);
                const statusValue = resolveStatusValue(u);
                const isInactive = statusValue === 0;
                const username =
                  resolveText(u, ["username", "userName", "login"]) || "—";
                const email = resolveText(u, ["email", "mail"]) || "—";
                const name = resolveName(u) || "—";
                const typ = resolveType(t, u) || "—";
                const statusLabel = getStatusLabel(t, statusValue);
                const lastLogin = resolveLastLogin(u);
                const lastLoginStr = lastLogin
                  ? formatDateTimeSecondsSk(lastLogin, "—")
                  : "—";

                return (
                  <TableRow key={String(id ?? idx)} hover>
                    <TableCell>{username}</TableCell>
                    <TableCell>{email}</TableCell>
                    <TableCell>{name}</TableCell>
                    <TableCell>{typ}</TableCell>
                    <TableCell>{statusLabel}</TableCell>
                    <TableCell>{lastLoginStr}</TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        <Tooltip
                          title={t("buttons.edit", { defaultValue: "Edit" })}
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => id && onEdit(id)}
                              disabled={!id}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {/* {!isInactive ? (
                          <Tooltip
                            title={t("buttons.delete", {
                              defaultValue: "Delete",
                            })}
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => onDelete(u)}
                                disabled={!id || isDeleting}
                                color="error"
                              >
                                {isDeleting ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DeleteOutlineIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null} */}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </Box>
  );
};

export default DSOKUsers;
